import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Component, Inject, OnDestroy } from "@angular/core";
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MatChipInputEvent } from "@angular/material/chips";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FileLogger } from "@comunicare/comunicare-helper";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { FormPage } from "src/app/pages/formPage";
import { TPDAsService } from "../../tpdas.service";

@Component({
  selector: "app-send-invites-modal",
  templateUrl: "./send-invites-modal.component.html",
  styleUrls: ["./send-invites-modal.component.scss"],
})
export class SendInvitesModalComponent extends FormPage implements OnDestroy {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  private onDestroy$ = new Subject<void>();
  public form = this.fb.group({
    emails: this.fb.control([], [this.requireOne, this.allValidEmails]),
  });
  public isSubmitting = false;

  constructor(
    private readonly dialogRef: MatDialogRef<boolean>,
    private tpdaService: TPDAsService,
    private translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private snackbar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      TPDAId: string;
    }
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public getForm(): UntypedFormGroup {
    return this.form;
  }

  private requireOne(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string[];
    return value && value.length > 0 ? null : { requireOne: true };
  }

  private allValidEmails(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string[];
    if (!value || value.length === 0) return null;
    const invalid = value.some((v) => {
      const invalid = !!Validators.email(new FormControl(v));
      return invalid ? true : false;
    });
    control.markAsTouched({ onlySelf: true });
    return invalid ? { invalidEmail: true } : null;
  }

  public addEmail(event: MatChipInputEvent): void {
    const input = event.chipInput?.inputElement;
    const value = (event.value || "").trim();

    if (value) {
      const emails = this.form.get("emails")!.value || [];
      this.form.get("emails")!.setValue([...emails, value]);
      this.form.get("emails")!.updateValueAndValidity();
    }

    if (input) input.value = "";
  }

  public removeEmail(email: string): void {
    const emails = this.form.get("emails")!.value || [];
    const index = emails.indexOf(email);

    if (index >= 0) {
      emails.splice(index, 1);
      this.form.get("emails")!.setValue([...emails]);
      this.form.get("emails")!.updateValueAndValidity();
    }
  }

  public async submit(): Promise<void> {
    if (this.form.valid) {
      this.isSubmitting = true;
      const emails = this.form.get("emails")!.value as string[];
      this.form.get("emails")!.disable();
      const missedEmails = await this.tpdaService
        .sendTPDAInvitations(this.data.TPDAId, emails, this.translateService.currentLang)
        .catch((err) => {
          FileLogger.error("Send invites modal", "submit()", err);
          this.snackbar.open(`❗${this.translateService.instant("api.errors.unknowerror")}`, "ok", {
            duration: 5000,
            horizontalPosition: "center",
            verticalPosition: "bottom",
          });
          this.form.get("emails")!.enable();
          this.isSubmitting = false;
          return null;
        });
      if (!missedEmails) return;
      if (missedEmails.length === 0) {
        this.snackbar.open(`✅ ${this.translateService.instant("tpda.emails.success")}`, "ok", {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "bottom",
        });
        this.dialogRef.close(true);
      } else {
        const missedEmailsStr = missedEmails.join(", ");
        this.snackbar.open(`❗${this.translateService.instant("tpda.emails.partialSuccess", { missedEmails: missedEmailsStr })}`, "ok", {
          duration: 7000,
          horizontalPosition: "center",
          verticalPosition: "bottom",
        });
      }
      this.form.get("emails")!.enable();
      this.isSubmitting = false;
    } else {
      this.validateAllFormFields(this.form);
    }
  }
}
